import type { FieldValues, Path, PathValue } from "react-hook-form";
import type { Organization } from "~care/types";

import React, { useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { organizationLocalName } from "~care/lib/utils/franchise-utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Select/Select";

type StringFieldName<T extends FieldValues> = {
  [K in Path<T>]: PathValue<T, K> extends string ? K : never;
}[Path<T>];

type NumberFieldName<T extends FieldValues> = {
  [K in Path<T>]: PathValue<T, K> extends number ? K : never;
}[Path<T>];

interface LocationSelectProps<T extends FieldValues> {
  franchiseOptions: Organization[];
  /**
   * Which field in your form is the final postal code stored into?
   * Must point to a `string` field in your Zod/React Hook Form schema.
   */
  postalCodeFieldName: StringFieldName<T>;

  /**
   * Which field in your form is the organization number stored into?
   * Must point to a `number` field in your Zod/React Hook Form schema.
   */
  organizationNumberFieldName: NumberFieldName<T>;

  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function LocationSelect<T extends FieldValues>({
  franchiseOptions,
  postalCodeFieldName,
  organizationNumberFieldName,
  open,
  onOpenChange,
}: LocationSelectProps<T>) {
  const { control, setValue, trigger } = useFormContext<T>();

  // Filter out duplicate location names, ignoring "Home Instead"
  const seen = new Set<string>();
  const uniqueFranchises = franchiseOptions.filter((organization) => {
    const localName = organizationLocalName(organization);
    if (!localName) return false;
    const cleanedLocalName = localName.trim();
    return !seen.has(cleanedLocalName) && seen.add(cleanedLocalName);
  });

  const orgNumber = useWatch({ name: organizationNumberFieldName });
  const firstFranchise = uniqueFranchises[0];

  React.useEffect(() => {
    if (!orgNumber && firstFranchise) {
      setValue(
        organizationNumberFieldName,
        firstFranchise.number as PathValue<
          T,
          typeof organizationNumberFieldName
        >,
        {
          shouldDirty: false,
        },
      );
      setValue(
        postalCodeFieldName,
        firstFranchise.offices[0].postalCode as PathValue<
          T,
          typeof postalCodeFieldName
        >,
        {
          shouldDirty: false,
        },
      );
    }
  }, [
    orgNumber,
    firstFranchise,
    setValue,
    organizationNumberFieldName,
    postalCodeFieldName,
  ]);

  // Handle changes to the organization select
  const handleOrganizationChange = (value: string) => {
    const orgNumber = Number(value);
    const selectedOrg = franchiseOptions.find(
      (org: Organization) => org.number === orgNumber,
    );

    if (Array.isArray(selectedOrg?.offices) && selectedOrg.offices.length > 0) {
      // Set both values when organization changes
      setValue(
        postalCodeFieldName,
        selectedOrg.offices[0].postalCode as PathValue<
          T,
          typeof postalCodeFieldName
        >,
        { shouldDirty: true, shouldValidate: true },
      );

      setValue(
        organizationNumberFieldName,
        orgNumber as PathValue<T, typeof organizationNumberFieldName>,
        { shouldDirty: true, shouldValidate: true },
      );
    }
  };

  // On initial load, set a default location if available
  useEffect(() => {
    if (Array.isArray(franchiseOptions) && franchiseOptions.length > 0) {
      const firstValidOrg = franchiseOptions.find((org) =>
        organizationLocalName(org),
      );

      if (
        firstValidOrg &&
        Array.isArray(firstValidOrg.offices) &&
        firstValidOrg.offices.length > 0
      ) {
        const orgNumber = Number(firstValidOrg.number);

        setValue(
          postalCodeFieldName,
          firstValidOrg.offices[0].postalCode as PathValue<
            T,
            typeof postalCodeFieldName
          >,
          { shouldDirty: true, shouldValidate: true },
        );

        setValue(
          organizationNumberFieldName,
          orgNumber as PathValue<T, typeof organizationNumberFieldName>,
          { shouldDirty: true, shouldValidate: true },
        );

        // Trigger validation after setting values
        trigger(organizationNumberFieldName);
      }
    }
  }, [
    franchiseOptions,
    setValue,
    postalCodeFieldName,
    organizationNumberFieldName,
    trigger,
  ]);

  return (
    <Controller
      control={control}
      name={organizationNumberFieldName}
      render={({ field }) => {
        const defaultOrgNumber = uniqueFranchises[0]?.number;
        const currentValue = field.value || defaultOrgNumber;

        return (
          <Select
            name={organizationNumberFieldName}
            onOpenChange={onOpenChange}
            onValueChange={(value: string) => {
              field.onChange(Number(value));
              handleOrganizationChange(value);
            }}
            open={open}
            value={String(currentValue)}
          >
            <SelectTrigger label="Location">
              <SelectValue
                placeholder={organizationLocalName(uniqueFranchises[0])}
              />
            </SelectTrigger>
            <SelectContent>
              {uniqueFranchises.map((organization) => (
                <SelectItem
                  key={organization.number}
                  value={String(organization.number)}
                >
                  {organizationLocalName(organization)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }}
    />
  );
}
